import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { getApiErrorMessage } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verifica token al mount
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const rolesRaw = payload.roles ?? payload.role ?? '';
        const rolesArr = typeof rolesRaw === 'string'
          ? rolesRaw.split(',').map((r) => (r || '').replace(/^ROLE_/, '').trim()).filter(Boolean)
          : Array.isArray(rolesRaw) ? rolesRaw.map((r) => String(r).replace(/^ROLE_/, '').trim()) : [];
        const role = rolesArr[0] ?? (payload.role || payload.roles || '').replace(/^ROLE_/, '');
        const userId = payload.userId ?? payload.id ?? localStorage.getItem('userId');
        const medicoId = payload.medicoId ?? localStorage.getItem('medicoId');
        if (userId != null) localStorage.setItem('userId', String(userId));
        if (medicoId != null) localStorage.setItem('medicoId', String(medicoId));
        setUser({
          email: payload.sub,
          role,
          roles: rolesArr.length > 0 ? rolesArr : (role ? [role] : []),
          userId: userId != null ? Number(userId) : null,
          medicoId: medicoId != null ? Number(medicoId) : null
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const data = response.data;
      const newToken = data.token ?? data.accessToken;
      if (!newToken) return { success: false, message: 'Risposta di login non valida' };
      const { refreshToken, email: userEmail, role, roles: rolesFromApi, userId, medicoId } = data;
      const rolesArr = Array.isArray(rolesFromApi)
        ? rolesFromApi.map((r) => String(r).replace(/^ROLE_/, '').trim())
        : (role != null && role !== '')
          ? role.split(',').map((r) => String(r).replace(/^ROLE_/, '').trim())
          : [];
      const fromToken = (() => {
        try {
          const payload = JSON.parse(atob(newToken.split('.')[1]));
          const raw = payload.roles ?? payload.role ?? '';
          return typeof raw === 'string'
            ? raw.split(',').map((r) => (r || '').replace(/^ROLE_/, '').trim()).filter(Boolean)
            : Array.isArray(raw) ? raw.map((r) => String(r).replace(/^ROLE_/, '').trim()) : [];
        } catch (e) { return []; }
      })();
      const roles = fromToken.length > 0 ? fromToken : rolesArr;
      const normalizedRole = roles[0] ?? (role || '').replace(/^ROLE_/, '');
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (userId != null) localStorage.setItem('userId', userId);
      if (medicoId != null) localStorage.setItem('medicoId', medicoId);
      setToken(newToken);
      setUser({ email: userEmail, role: normalizedRole, roles, userId, medicoId });
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, role: normalizedRole, roles };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: getApiErrorMessage(error, 'Accesso non riuscito. Verifica email e password.')
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('medicoId');
    localStorage.removeItem('impersonation.admin.token');
    localStorage.removeItem('impersonation.admin.refreshToken');
    localStorage.removeItem('impersonation.admin.userId');
    localStorage.removeItem('impersonation.admin.medicoId');
    localStorage.removeItem('impersonation.patient.id');
    localStorage.removeItem('impersonation.patient.nome');
    localStorage.removeItem('impersonation.patient.cognome');
    localStorage.removeItem('impersonation.patient.email');
    localStorage.removeItem('impersonation.medico.id');
    localStorage.removeItem('impersonation.medico.nome');
    localStorage.removeItem('impersonation.medico.cognome');
    localStorage.removeItem('impersonation.medico.email');
    localStorage.removeItem('impersonation.medico.idGenere');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const startPatientImpersonation = (authPayload, patientMeta = null) => {
    const nextToken = authPayload?.token ?? authPayload?.accessToken;
    if (!nextToken) {
      return { success: false, message: 'Token impersonazione non valido' };
    }

    const currentToken = localStorage.getItem('token');
    if (currentToken) localStorage.setItem('impersonation.admin.token', currentToken);
    const currentRefresh = localStorage.getItem('refreshToken');
    if (currentRefresh) localStorage.setItem('impersonation.admin.refreshToken', currentRefresh);
    const currentUserId = localStorage.getItem('userId');
    if (currentUserId) localStorage.setItem('impersonation.admin.userId', currentUserId);
    const currentMedicoId = localStorage.getItem('medicoId');
    if (currentMedicoId) localStorage.setItem('impersonation.admin.medicoId', currentMedicoId);

    localStorage.setItem('token', nextToken);
    if (authPayload?.refreshToken) localStorage.setItem('refreshToken', authPayload.refreshToken);
    else localStorage.removeItem('refreshToken');
    if (authPayload?.userId != null) localStorage.setItem('userId', String(authPayload.userId));
    else localStorage.removeItem('userId');
    localStorage.removeItem('medicoId');
    if (patientMeta?.id != null) localStorage.setItem('impersonation.patient.id', String(patientMeta.id));
    if (patientMeta?.nome) localStorage.setItem('impersonation.patient.nome', String(patientMeta.nome));
    if (patientMeta?.cognome) localStorage.setItem('impersonation.patient.cognome', String(patientMeta.cognome));
    if (patientMeta?.email) localStorage.setItem('impersonation.patient.email', String(patientMeta.email));

    setToken(nextToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    return { success: true };
  };

  const startMedicoImpersonation = (authPayload, medicoMeta = null) => {
    const nextToken = authPayload?.token ?? authPayload?.accessToken;
    if (!nextToken) {
      return { success: false, message: 'Token impersonazione non valido' };
    }

    const currentToken = localStorage.getItem('token');
    if (currentToken) localStorage.setItem('impersonation.admin.token', currentToken);
    const currentRefresh = localStorage.getItem('refreshToken');
    if (currentRefresh) localStorage.setItem('impersonation.admin.refreshToken', currentRefresh);
    const currentUserId = localStorage.getItem('userId');
    if (currentUserId) localStorage.setItem('impersonation.admin.userId', currentUserId);
    const currentMedicoId = localStorage.getItem('medicoId');
    if (currentMedicoId) localStorage.setItem('impersonation.admin.medicoId', currentMedicoId);

    localStorage.setItem('token', nextToken);
    if (authPayload?.refreshToken) localStorage.setItem('refreshToken', authPayload.refreshToken);
    else localStorage.removeItem('refreshToken');
    if (authPayload?.userId != null) localStorage.setItem('userId', String(authPayload.userId));
    else localStorage.removeItem('userId');
    if (authPayload?.medicoId != null) localStorage.setItem('medicoId', String(authPayload.medicoId));
    else localStorage.removeItem('medicoId');
    if (medicoMeta?.id != null) localStorage.setItem('impersonation.medico.id', String(medicoMeta.id));
    if (medicoMeta?.nome) localStorage.setItem('impersonation.medico.nome', String(medicoMeta.nome));
    if (medicoMeta?.cognome) localStorage.setItem('impersonation.medico.cognome', String(medicoMeta.cognome));
    if (medicoMeta?.email) localStorage.setItem('impersonation.medico.email', String(medicoMeta.email));
    if (medicoMeta?.idGenere != null) localStorage.setItem('impersonation.medico.idGenere', String(medicoMeta.idGenere));

    setToken(nextToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
    return { success: true };
  };

  const stopImpersonation = () => {
    const adminToken = localStorage.getItem('impersonation.admin.token');
    if (!adminToken) {
      return { success: false, message: 'Nessuna impersonificazione attiva' };
    }

    localStorage.setItem('token', adminToken);
    const adminRefresh = localStorage.getItem('impersonation.admin.refreshToken');
    if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);
    else localStorage.removeItem('refreshToken');
    const adminUserId = localStorage.getItem('impersonation.admin.userId');
    if (adminUserId) localStorage.setItem('userId', adminUserId);
    else localStorage.removeItem('userId');
    const adminMedicoId = localStorage.getItem('impersonation.admin.medicoId');
    if (adminMedicoId) localStorage.setItem('medicoId', adminMedicoId);
    else localStorage.removeItem('medicoId');

    localStorage.removeItem('impersonation.admin.token');
    localStorage.removeItem('impersonation.admin.refreshToken');
    localStorage.removeItem('impersonation.admin.userId');
    localStorage.removeItem('impersonation.admin.medicoId');
    localStorage.removeItem('impersonation.patient.id');
    localStorage.removeItem('impersonation.patient.nome');
    localStorage.removeItem('impersonation.patient.cognome');
    localStorage.removeItem('impersonation.patient.email');
    localStorage.removeItem('impersonation.medico.id');
    localStorage.removeItem('impersonation.medico.nome');
    localStorage.removeItem('impersonation.medico.cognome');
    localStorage.removeItem('impersonation.medico.email');
    localStorage.removeItem('impersonation.medico.idGenere');

    setToken(adminToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    return { success: true };
  };

  const isImpersonating = () => !!localStorage.getItem('impersonation.admin.token');
  const getImpersonatedPatientDisplayName = () => {
    const nome = (localStorage.getItem('impersonation.patient.nome') || '').trim();
    const cognome = (localStorage.getItem('impersonation.patient.cognome') || '').trim();
    const full = `${nome} ${cognome}`.trim();
    if (full) return full;
    const email = (localStorage.getItem('impersonation.patient.email') || '').trim();
    if (email) return email;
    return 'paziente';
  };

  const getImpersonatedMedicoDisplayName = () => {
    const nome = (localStorage.getItem('impersonation.medico.nome') || '').trim();
    const cognome = (localStorage.getItem('impersonation.medico.cognome') || '').trim();
    const full = `${nome} ${cognome}`.trim();
    const idGenereRaw = localStorage.getItem('impersonation.medico.idGenere');
    const title = String(idGenereRaw) === '2' ? 'Dott.ssa' : 'Dott.';
    if (full) return `${title} ${full}`.trim();
    const email = (localStorage.getItem('impersonation.medico.email') || '').trim();
    if (email) return email;
    return 'medico';
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isAdmin = () => getRoles().includes('ADMIN');
  const isMerchant = () => getRoles().includes('MERCHANT');
  const isMedico = () => getRoles().includes('MEDICO');

  const isPaziente = () => {
    const roles = getRoles();
    return roles.includes('PAZIENTE');
  };

  /** Elenco ruoli senza prefisso ROLE_ (per multi-ruolo e pagina Scegli area).
   * Usa sempre il token come fonte di verità quando presente, così si vedono tutti i ruoli (es. ADMIN + PAZIENTE). */
  const getRoles = () => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const rolesRaw = payload.roles ?? payload.role ?? '';
        const arr = typeof rolesRaw === 'string'
          ? rolesRaw.split(',').map((r) => (r || '').replace(/^ROLE_/, '').trim()).filter(Boolean)
          : Array.isArray(rolesRaw) ? rolesRaw.map((r) => String(r).replace(/^ROLE_/, '').trim()) : [];
        if (arr.length > 0) return arr;
      } catch (e) { /* ignore */ }
    }
    if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
    if (user?.role) return [user.role];
    return [];
  };

  const getUserId = () => {
    return user?.userId ?? localStorage.getItem('userId');
  };

  /** Id medico per l'area medico (presente se l'utente ha ruolo MEDICO; per ADMIN+MEDICO è distinto da userId). */
  const getMedicoId = () => {
    const id = user?.medicoId ?? localStorage.getItem('medicoId');
    return id != null ? Number(id) : null;
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await api.post('/auth/google', { idToken });
      const data = response.data;
      const newToken = data.token ?? data.accessToken;
      const { refreshToken, email: userEmail, role, userId } = data;
      const normalizedRole = (role || '').replace(/^ROLE_/, '');
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (userId != null) localStorage.setItem('userId', userId);
      setToken(newToken);
      setUser({ email: userEmail, role: normalizedRole, userId });
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, role: normalizedRole };
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, message: error.response?.data?.message || 'Accesso con Google fallito' };
    }
  };

  const loginWithApple = async (idToken) => {
    try {
      const response = await api.post('/auth/apple', { idToken });
      const data = response.data;
      const newToken = data.token ?? data.accessToken;
      const { refreshToken, email: userEmail, role, userId } = data;
      const normalizedRole = (role || '').replace(/^ROLE_/, '');
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (userId != null) localStorage.setItem('userId', userId);
      setToken(newToken);
      setUser({ email: userEmail, role: normalizedRole, userId });
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, role: normalizedRole };
    } catch (error) {
      console.error('Apple login error:', error);
      return { success: false, message: error.response?.data?.message || 'Accesso con Apple fallito' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const data = response.data;
      if (data.message && !data.accessToken) {
        return { success: true, message: data.message, requireEmailConfirmation: true };
      }
      const newToken = data.accessToken ?? data.token;
      const { refreshToken, email: userEmail, role, roles: rolesFromApi, userId } = data;
      const rolesArr = Array.isArray(rolesFromApi)
        ? rolesFromApi.map((r) => String(r).replace(/^ROLE_/, '').trim())
        : (role != null && role !== '')
          ? role.split(',').map((r) => String(r).replace(/^ROLE_/, '').trim())
          : [];
      const normalizedRole = rolesArr[0] ?? (role || '').replace(/^ROLE_/, '');
      localStorage.setItem('token', newToken);
      localStorage.setItem('refreshToken', refreshToken);
      if (userId != null) localStorage.setItem('userId', userId);
      setToken(newToken);
      setUser({ email: userEmail, role: normalizedRole, roles: rolesArr, userId });
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, role: normalizedRole };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Registrazione fallita'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithApple,
    logout,
    isAuthenticated,
    isAdmin,
    isMerchant,
    isMedico,
    isPaziente,
    getRoles,
    getUserId,
    getMedicoId,
    startPatientImpersonation,
    startMedicoImpersonation,
    stopImpersonation,
    isImpersonating,
    getImpersonatedPatientDisplayName,
    getImpersonatedMedicoDisplayName
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

